import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { SparklesIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const AIReadingDisplay = ({ interpretation, isLoading, topic, cards = [], spreadName = "Tirada de Oráculo", onClose }) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const pdfRef = useRef(null);
  
  // Estado para guardar las imágenes convertidas a Base64
  const [base64Images, setBase64Images] = useState({});

  // Función auxiliar para convertir URL a Base64
const convertImageToBase64 = async (url) => {
  try {
    // ESTA LÍNEA ES EL CAMBIO CLAVE: Añade ?t=numero_aleatorio
    const cacheBustUrl = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
    
    const response = await fetch(cacheBustUrl, { 
      mode: 'cors' // Forzamos que use el protocolo CORS
    });
    
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Fallo CORS:", url);
    return url;
  }
};
const handleDownloadPDF = async () => {
    // 1. Activamos el estado primero (hace que la plantilla se monte en el DOM)
    setIsGeneratingPdf(true);

    try {
      // 1. PRE-PROCESAMIENTO DE IMÁGENES
      const imageMap = {};
      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        if (card.image) {
          const base64 = await convertImageToBase64(card.image);
          imageMap[i] = base64;
        }
      }
      setBase64Images(imageMap);

      // Esta pausa de 1 segundo da tiempo a que React monte la plantilla y las imágenes
      await new Promise(resolve => setTimeout(resolve, 1000));

      const element = pdfRef.current;
      if (!element) {
        throw new Error("No se pudo obtener el contenedor del PDF");
      }
      
      // 2. GENERAR CANVAS
      const canvas = await html2canvas(element, {
        scale: 2, 
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.querySelector('.pdf-container');
            if (clonedElement) {
                clonedElement.style.display = 'block';
            }
        }
      });

      // 3. CREAR PDF
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const dateStr = new Date().toISOString().split('T')[0];
      const safeTopic = topic ? topic.replace(/[^a-z0-9]/gi, '_').substring(0, 20) : 'Lectura';
      pdf.save(`Oraculo_${safeTopic}_${dateStr}.pdf`);

    } catch (error) {
      console.error("Error generando PDF:", error);
      alert("Hubo un error al generar el PDF. Por favor intenta de nuevo.");
    } finally {
      setIsGeneratingPdf(false); // Al terminar, se desmonta y desaparece del DOM
    }
  };
  if (!isLoading && !interpretation) return null;

  return (
    <div className="w-full max-w-4xl mx-auto mt-12 animate-fade-in">
      <div className="relative bg-gray-900/90 backdrop-blur-md border border-purple-500/30 rounded-2xl p-6 md:p-8 shadow-2xl shadow-purple-900/20">
        
        {/* Header Visual (Pantalla) */}
        <div className="flex items-center justify-between mb-8 border-b border-gray-700 pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-900/50 rounded-lg border border-purple-500/20">
              <SparklesIcon className={`w-6 h-6 text-purple-300 ${isLoading ? 'animate-spin' : ''}`} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-200 to-sky-200">
                {isLoading ? 'Consultando al Oráculo...' : 'Mensaje de los Guías'}
              </h3>
            </div>
          </div>
          
          <div className="flex gap-2">
            {!isLoading && (
              <button 
                onClick={handleDownloadPDF}
                disabled={isGeneratingPdf}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-sky-200 px-3 py-2 rounded-lg transition-colors border border-gray-600 text-sm font-medium disabled:opacity-50"
              >
                {isGeneratingPdf ? (
                    <span className="animate-pulse">Preparando...</span>
                ) : (
                    <>
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        PDF
                    </>
                )}
              </button>
            )}

            {onClose && (
              <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full">
                <XMarkIcon className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>

        {/* Contenido Visual (Pantalla) */}
        <div className="min-h-[200px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full py-12 gap-6">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-400 rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-sky-500/20 border-b-sky-400 rounded-full animate-spin-reverse scale-75"></div>
              </div>
              <p className="text-gray-400 animate-pulse text-sm font-medium tracking-wide">
                Interpretando las energías de "{topic}"...
              </p>
            </div>
          ) : (
            <div className="text-gray-200 leading-relaxed font-light">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-2xl md:text-3xl font-bold text-purple-200 mt-8 mb-4 border-b border-purple-500/30 pb-2" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-xl md:text-2xl font-bold text-sky-200 mt-8 mb-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-lg md:text-xl font-semibold text-purple-300 mt-6 mb-3 flex items-center gap-2" {...props} />,
                  p: ({node, ...props}) => <p className="mb-4 text-gray-300 leading-7 text-[15px] md:text-base" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-pink-300" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-none space-y-2 mb-6 ml-1" {...props} />,
                  li: ({node, ...props}) => (
                    <li className="relative pl-6 text-gray-300" {...props}>
                      <span className="absolute left-0 top-2 w-1.5 h-1.5 bg-sky-400 rounded-full shadow-[0_0_8px_rgba(56,189,248,0.8)]"></span>
                      {props.children}
                    </li>
                  ),
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-l-4 border-purple-500/50 pl-4 py-1 my-6 bg-purple-900/10 italic text-gray-400 rounded-r-lg" {...props} />
                  ),
                }}
              >
                {interpretation}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
{/* --- PLANTILLA OCULTA PARA PDF --- */}
      {/* Solo existe en el DOM los 2 segundos que dura la descarga */}
      {isGeneratingPdf && (
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: '-9999px', 
            pointerEvents: 'none', 
            zIndex: -9999 
          }}
          aria-hidden="true"
        >
           <div 
             ref={pdfRef} 
             className="pdf-container w-[794px] min-h-[1123px] bg-white text-gray-800 p-12 font-serif relative"
             style={{ display: 'block' }}
           >
                {/* PDF Header */}
                <div className="border-b-2 border-purple-900 pb-6 mb-8 flex justify-between items-start">
                    <div>
                        <h1 className="text-3xl font-bold text-purple-900 uppercase tracking-widest mb-2">{spreadName}</h1>
                        <p className="text-gray-500 text-sm">Fecha: {new Date().toLocaleDateString()}</p>
                    </div>
                    <div className="text-right max-w-xs">
                        <p className="text-sm text-gray-500 uppercase tracking-wide">Tema consultado</p>
                        <h2 className="text-xl font-bold text-gray-800 italic">"{topic}"</h2>
                    </div>
                </div>

                {/* PDF Cards Display */}
                {cards && cards.length > 0 && (
                    <div className="mb-10">
                        <h3 className="text-sm font-bold text-purple-900 uppercase border-b border-gray-200 pb-2 mb-6">Cartas Reveladas</h3>
                        <div className="flex flex-wrap justify-center gap-6">
                            {cards.map((c, i) => (
                                <div key={i} className="flex flex-col items-center w-32 break-inside-avoid">
                                    <div className="text-xs font-bold text-purple-700 uppercase mb-1">{c.positionName || `Posición ${i+1}`}</div>
                                    
                                    <div className="w-24 h-36 border border-gray-300 rounded overflow-hidden shadow-sm relative bg-gray-100">
                                        <img 
                                            src={base64Images[i] || c.image} 
                                            alt={c.data?.name || "Carta"} 
                                            className={`w-full h-full object-cover ${c.isReversed ? 'rotate-180' : ''}`}
                                            crossOrigin="anonymous" 
                                        />
                                    </div>
                                    <div className="text-center mt-2">
                                        <p className="text-xs font-bold text-gray-800 leading-tight">{c.data?.name}</p>
                                        {c.isReversed && <p className="text-[10px] text-red-600 font-semibold uppercase">Invertida</p>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* PDF Interpretation Text */}
                <div className="prose prose-sm max-w-none text-justify text-gray-700">
                    <ReactMarkdown
                        components={{
                            h1: ({node, ...props}) => <h3 className="text-lg font-bold text-purple-900 mt-6 mb-2 uppercase" {...props} />,
                            h2: ({node, ...props}) => <h4 className="text-md font-bold text-purple-800 mt-4 mb-2" {...props} />,
                            h3: ({node, ...props}) => <h5 className="text-sm font-bold text-gray-900 mt-3 mb-1" {...props} />,
                            strong: ({node, ...props}) => <span className="font-bold text-purple-900" {...props} />,
                            li: ({node, ...props}) => <li className="mb-1 list-disc ml-4 marker:text-purple-500" {...props} />,
                            p: ({node, ...props}) => <p className="mb-2" {...props} />,
                        }}
                    >
                        {interpretation}
                    </ReactMarkdown>
                </div>

                {/* PDF Footer */}
                <div className="mt-12 pt-6 border-t border-gray-300 text-center">
                    <p className="text-xs text-gray-400 italic">Lectura generada por Oráculo Digital. Confía en tu intuición.</p>
                </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default AIReadingDisplay;